<script setup>
import { ref, toRef, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useInbox } from 'dashboard/composables/useInbox';
import {
  useReplyActions,
  RECORDING_STATE,
} from 'dashboard/composables/useReplyActions';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import JitsiMeetButton from '../JitsiMeetButton.vue';

import AIAssistanceButton from '../AIAssistanceButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioRecorder from './AudioRecorder.vue';

// Props
const props = defineProps({
  isNote: { type: Boolean, default: false },
  onSend: { type: Function, default: () => {} },
  sendButtonText: { type: String, default: '' },
  recordingAudioDurationText: { type: String, default: '00:00' },
  inbox: { type: Object, default: () => ({}) },
  showFileUpload: { type: Boolean, default: false },
  showAudioRecorder: { type: Boolean, default: false },
  onFileUpload: { type: Function, default: () => {} },
  toggleEmojiPicker: { type: Function, default: () => {} },
  toggleAudioRecorder: { type: Function, default: () => {} },
  isRecordingAudio: { type: Boolean, default: false },
  recordingAudioState: { type: String, default: '' },
  isSendDisabled: { type: Boolean, default: false },
  isOnPrivateNote: { type: Boolean, default: false },
  enableMultipleFileUpload: { type: Boolean, default: true },
  enableWhatsAppTemplates: { type: Boolean, default: false },
  enableContentTemplates: { type: Boolean, default: false },
  conversationId: { type: [String, Number], required: true },
  message: { type: String, default: '' },
  newConversationModalActive: { type: Boolean, default: false },
  portalSlug: { type: String, required: true },
  conversationType: { type: String, default: '' },
  showQuotedReplyToggle: { type: Boolean, default: false },
  quotedReplyEnabled: { type: Boolean, default: false },
  hasAttachments: { type: Boolean, default: false },
  hasStoppedRecording: { type: Boolean, default: false },
  audioRecordFormat: { type: String, default: 'audio/wav' },
});

// Emits
const emit = defineEmits([
  'replaceText',
  'toggleInsertArticle',
  'selectWhatsappTemplate',
  'selectContentTemplate',
  'toggleQuotedReply',
  'recorderProgressChanged',
  'finishRecord',
  'play',
  'pause',
  'sendAudio',
  'sendAudioNow',
]);

// i18n
const { t } = useI18n();

// Store
const store = useStore();
const uiFlags = computed(() => store.getters['integrations/getUIFlags']);
const accountId = computed(() => store.getters?.getCurrentAccountId);
const isFeatureEnabledonAccount = (accId, feature) =>
  store.getters['accounts/isFeatureEnabledonAccount']?.(accId, feature);
const isFetchingAppIntegrations = computed(() => uiFlags.value?.isFetching);

// Refs
const uploadRef = ref(null);
const audioRecorderInput = ref(null);

// Composables
const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
  useUISettings();
const inboxRef = toRef(props, 'inbox');
const {
  channelType,
  isALineChannel,
  isAnInstagramChannel,
  isAPIInbox,
  isAWebWidgetInbox,
} = useInbox(inboxRef);

// Reactive props as refs for composable
const messageRef = toRef(props, 'message');
const hasAttachmentsRef = toRef(props, 'hasAttachments');
const isRecordingAudioRef = toRef(props, 'isRecordingAudio');
const isNoteRef = toRef(props, 'isNote');
const isSendDisabledRef = toRef(props, 'isSendDisabled');

// Audio Recorder Enabled
const isAudioRecorderEnabled = computed(() => {
  if (isALineChannel.value) return false;
  return isFeatureEnabledonAccount(
    accountId.value,
    FEATURE_FLAGS.VOICE_RECORDER
  );
});

// Stop recording and send
const stopAndSendAudio = () => {
  if (audioRecorderInput.value) {
    audioRecorderInput.value.stopRecording();
  }
};

// Reply Actions Composable
const {
  showRecordButton,
  sendButtonIcon,
  sendButtonColor,
  isSendingAudio,
  handleMainButtonClick,
  handleFinishRecording,
} = useReplyActions({
  message: messageRef,
  hasAttachments: hasAttachmentsRef,
  isRecordingAudio: isRecordingAudioRef,
  isNote: isNoteRef,
  isAudioRecorderEnabled,
  isSendDisabled: isSendDisabledRef,
  onSend: props.onSend,
  toggleAudioRecorder: props.toggleAudioRecorder,
  onSendAudio: stopAndSendAudio,
});

// UI State Computed
const wrapClass = computed(() => ({
  'is-note-mode': props.isNote,
  'is-recording': props.isRecordingAudio,
}));

const showAttachButton = computed(() => props.showFileUpload || props.isNote);

const isInstagramDM = computed(
  () => props.conversationType === 'instagram_direct_message'
);

const allowedFileTypes = computed(() => {
  if (props.isOnPrivateNote) return ALLOWED_FILE_TYPES;
  let channel = channelType.value || props.inbox?.channel_type;
  if (isAnInstagramChannel.value || isInstagramDM.value) {
    channel = INBOX_TYPES.INSTAGRAM;
  }
  return getAllowedFileTypesByChannel({
    channelType: channel,
    medium: props.inbox?.medium,
  });
});

const enableDragAndDrop = computed(() => !props.newConversationModalActive);

const showMessageSignatureButton = computed(() => !props.isOnPrivateNote);

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(channelType.value)
);

const signatureToggleTooltip = computed(() =>
  sendWithSignature.value
    ? t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
    : t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP')
);

const quotedReplyToggleTooltip = computed(() =>
  props.quotedReplyEnabled
    ? t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
    : t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP')
);

const enableInsertArticleInReply = computed(() => props.portalSlug);

const showAudioPlayStopButton = computed(
  () => props.showAudioRecorder && props.isRecordingAudio
);

// Local icon logic that uses hasStoppedRecording
const localAudioRecorderPlayStopIcon = computed(() => {
  if (!props.hasStoppedRecording) {
    // Still recording - show stop icon
    return 'i-ph-stop';
  }
  // Already stopped - show play/pause based on state
  if (props.recordingAudioState === RECORDING_STATE.PLAYING) {
    return 'i-ph-pause';
  }
  return 'i-ph-play';
});

// Keyboard Events
useKeyboardEvents({
  '$mod+Alt+KeyA': {
    action: () => {
      if (uploadRef.value?.$el) {
        const input = uploadRef.value.$el.querySelector('input');
        input?.click();
      }
    },
    allowOnFocusedInput: true,
  },
});

// Methods
const toggleMessageSignature = () => {
  setSignatureFlagForInbox(channelType.value, !sendWithSignature.value);
};

const replaceText = text => emit('replaceText', text);
const toggleInsertArticle = () => emit('toggleInsertArticle');

const onRecorderProgressChanged = duration =>
  emit('recorderProgressChanged', duration);

const onFinishRecorder = file => {
  handleFinishRecording(
    file,
    f => emit('sendAudio', f),
    f => emit('finishRecord', f)
  );
};

const toggleAudioRecorderPlayPause = () => {
  if (audioRecorderInput.value) {
    if (!props.hasStoppedRecording) {
      // Still recording - stop it first
      audioRecorderInput.value.stopRecording();
    } else {
      // Already stopped - play/pause playback
      audioRecorderInput.value.playPause();
      if (props.recordingAudioState === RECORDING_STATE.PLAYING) {
        emit('pause');
      } else {
        emit('play');
      }
    }
  }
};

const handleSendAudio = () => {
  // If already stopped (user clicked Stop/Pause first)
  // File is already attached, just send directly
  if (props.hasStoppedRecording) {
    emit('sendAudioNow');
    return;
  }

  // Otherwise, stop recording first (sets isSendingAudio for callback)
  isSendingAudio.value = true;
  stopAndSendAudio();
};

// Lifecycle
onMounted(() => {
  ActiveStorage.start();
});
</script>

<template>
  <div class="flex justify-between p-3" :class="wrapClass">
    <!-- RECORDING MODE LAYOUT -->
    <template v-if="isRecordingAudio">
      <div class="left-wrap">
        <NextButton
          icon="i-ph-trash"
          color="ruby"
          faded
          sm
          @click="toggleAudioRecorder"
        />
      </div>
      <div class="center-wrap flex-grow px-2 flex items-center">
        <AudioRecorder
          ref="audioRecorderInput"
          class="w-full"
          :audio-record-format="audioRecordFormat"
          @recorder-progress-changed="onRecorderProgressChanged"
          @finish-record="onFinishRecorder"
          @play="emit('play')"
          @pause="emit('pause')"
        />
        <div class="text-xs text-n-slate-11 ml-2 font-mono whitespace-nowrap">
          {{ recordingAudioDurationText }}
        </div>
      </div>
      <div class="right-wrap gap-2">
        <NextButton
          v-if="showAudioPlayStopButton"
          :icon="localAudioRecorderPlayStopIcon"
          slate
          faded
          sm
          @click="toggleAudioRecorderPlayPause"
        />
        <NextButton
          icon="i-ph-paper-plane-right"
          color="teal"
          sm
          @click="handleSendAudio"
        />
      </div>
    </template>

    <!-- STANDARD MODE LAYOUT -->
    <template v-else>
      <div class="left-wrap">
        <NextButton
          v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-ph-smiley-sticker"
          slate
          faded
          sm
          @click="toggleEmojiPicker"
        />
        <FileUpload
          ref="uploadRef"
          v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          input-id="conversationAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="enableDragAndDrop"
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <NextButton
            v-if="showAttachButton"
            v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="i-ph-paperclip"
            slate
            faded
            sm
          />
        </FileUpload>

        <!-- v6.4.61: Botão de assinatura comentado (feature deprecated desde v6.3.97, restaurado pelo merge)
        <NextButton
          v-if="showMessageSignatureButton"
          v-tooltip.top-end="signatureToggleTooltip"
          icon="i-ph-signature"
          slate
          faded
          sm
          @click="toggleMessageSignature"
        />
        -->
        <NextButton
          v-if="showQuotedReplyToggle"
          v-tooltip.top-end="quotedReplyToggleTooltip"
          icon="i-ph-quotes"
          :variant="quotedReplyEnabled ? 'solid' : 'faded'"
          color="slate"
          sm
          :aria-pressed="quotedReplyEnabled"
          @click="emit('toggleQuotedReply')"
        />
        <NextButton
          v-if="enableWhatsAppTemplates"
          v-tooltip.top-end="t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="emit('selectWhatsappTemplate')"
        />
        <NextButton
          v-if="enableContentTemplates"
          v-tooltip.top-end="'Content Templates'"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="emit('selectContentTemplate')"
        />
        <JitsiMeetButton
          :conversation-id="conversationId"
          :is-private-note="isOnPrivateNote"
        />
        <VideoCallButton
          v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
          :conversation-id="conversationId"
        />
        <AIAssistanceButton
          v-if="!isFetchingAppIntegrations"
          :conversation-id="conversationId"
          :is-private-note="isOnPrivateNote"
          :message="message"
          @replace-text="replaceText"
        />
        <transition name="modal-fade">
          <div
            v-show="uploadRef && uploadRef.dropActive"
            class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
          >
            <fluent-icon icon="cloud-backup" size="40" />
            <h4 class="text-2xl break-words text-n-slate-12">
              {{ t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
            </h4>
          </div>
        </transition>
        <NextButton
          v-if="enableInsertArticleInReply"
          v-tooltip.top-end="
            t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
          "
          icon="i-ph-article-ny-times"
          slate
          faded
          sm
          @click="toggleInsertArticle"
        />
      </div>
      <div class="right-wrap">
        <NextButton
          :key="showRecordButton ? 'mic-btn' : 'send-btn'"
          :label="showRecordButton ? '' : sendButtonText || ''"
          :icon="showRecordButton || !sendButtonText ? sendButtonIcon : ''"
          type="submit"
          sm
          :color="sendButtonColor"
          :disabled="!showRecordButton && isSendDisabled"
          class="flex-shrink-0"
          :style="
            showRecordButton
              ? { backgroundColor: '#0d9c8a', color: '#ffffff' }
              : {}
          "
          @click="handleMainButtonClick"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.center-wrap {
  @apply flex items-center justify-center;
}

.right-wrap {
  @apply flex items-center;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
